import Vue from 'vue'
import VueAxios from 'vue-axios'
import VueRouter from 'vue-router'
import { first, once } from 'lodash'
import { jwt } from '../helpers'
import axios from 'axios'
import store from '../store'
import * as Constants from '../constants'
import * as Types from '../store/types'
import './interceptors'

Vue.use(VueAxios, axios)
Vue.use(VueRouter)

//------------------------------------------------------------

const RouterView =
{
	render(h)
	{
		return h('router-view')
	}
}

const Component = path => () => import(/* webpackChunkName: "pages/[request]"*/`@/pages/${path}`)
//const Component = path => () => require(`@/pages/${path}`).default

const routes =
[
	{
		path:      '/',
		component: Component('App'),
		meta:      { breadcrumb: true },
		children:
		[
			{
				path:     '/',
				name:     'home',
				redirect: { name: 'orders' },
			},

			//------------------------------------------------------------
			// Authentication
			//------------------------------------------------------------

			{
				path:      'auth',
				component: RouterView,
				children:
				[
					{
						path:      'forgot-password',
						alias:     'forgot',
						name:      'auth.forgot',
						component: Component('auth/ForgotPassword'),
						meta:      { guest: true },
					},

					{
						path:      'reset-password/:token',
						alias:     'reset',
						name:      'auth.reset',
						component: Component('auth/components/ResetPassword'),
						props:     true,
						meta:      { guest: true },
					},

					{
						path:      'register',
						name:      'auth.register',
						component: Component('auth/Register'),
						meta:      { guest: true },
					},

					{
						path:      'login',
						name:      'auth.login',
						component: Component('auth/Login'),
						meta:      { guest: true },
					},

					{
						path:      'verification',
						name:      'auth.verification',
						component: Component('auth/TwoFactorVerification'),
						meta:      { guest: true },
					},

					{
						path:      'logout',
						name:      'auth.logout',
						component: Component('auth/Logout'),
					},

					{
						path:      'employee/logout',
						name:      'auth.employee.logout',
						component: Component('auth/EmployeeLogout'),
					},
				],
			},

			//------------------------------------------------------------
			// Profile
			//------------------------------------------------------------

			{
				path:      'profile',
				component: Component('profile/ProfileLayout'),
				meta:      { auth: true },
				children:
				[
					{
						path:      '/',
						name:      'profile',
						component: Component('profile/Overview'),
					},

					{
						path:      'update',
						name:      'profile.update',
						component: Component('profile/Update'),
					},

					{
						path:      'two-factor',
						name:      'profile.two-factor',
						component: Component('profile/TwoFactor'),
					},

					{
						path:      'contact-methods',
						component: RouterView,
						children:
						[
							{
								path:      '/',
								name:      'profile.contact-methods',
								component: Component('ccdb/ContactMethods'),
								props:     route => ({ ccdbId: store.state.authUser.ccdb_id }),
							},

							{
								path:      'create',
								name:      'profile.contact-methods.create',
								component: Component('ccdb/ContactMethodForm'),
								props:     route => ({ ccdbId: store.state.authUser.ccdb_id }),
							},

							{
								path:      ':contactMethodId',
								name:      'profile.contact-methods.update',
								component: Component('ccdb/ContactMethodForm'),
								props:     route => ({ ccdbId: store.state.authUser.ccdb_id, ...(route.params) }),
							},
						],
					},
				],
			},

			//------------------------------------------------------------
			// Orders
			//------------------------------------------------------------

			{
				path:      'orders',
				component: RouterView,
				meta:      { auth: true },
				children:
				[
					{
						path:      '/',
						name:      'orders',
						component: Component('orders/Listing'),
					},
					{
						path:      '/usage',
						name:      'usage',
						component: Component('orders/SelectOrderForUsage')
					},

					{
						path:      ':orderId',
						component: Component('orders/Layout'),
						props:     true,
						children:
						[
							{
								path:      '/',
								name:      'orders.details',
								component: Component('orders/Details'),
								props:     true,
							},

							{
								path:      'statistics',
								name:      'orders.statistics',
								redirect:  route =>
								({
									name: 'statistics.order',
									params:
									{
										orderId:         route.params.orderId,
										statisticType:   first(Constants.STATISTIC_TYPES),
										statisticPeriod: first(Constants.STATISTIC_PERIODS),
									}
								}),
							},

							{
								path:      'maintenance',
								component: RouterView,
								props:     true,
								children:
								[
									{
										path:      '/',
										name:      'orders.maintenance',
										redirect:  route =>
										({
											name: 'maintenance',
											params:
											{
												orderId:         route.params.orderId,
											},
											query:
											{
												filter:          route.params.orderId,
											}
										}),
									},

									{
										path:      'create',
										name:      'orders.maintenance.create',
										component: Component('maintenance/Create'),
									},

									{
										path:      ':workid',
										name:      'orders.maintenance.details',
										component: Component('maintenance/Create'),
									},
								],
							},

							{
								path:      'diagnostics',
								name:      'orders.diagnostics',
								redirect:  route =>
								({
									name: 'network.diagnostics',
									params:
									{
										orderId:         route.params.orderId,
									}
								}),
							},

							{
								path:      'track',
								name:      'orders.track',
								component: Component('orders/Track'),
								props:     true,
							},

							{
								path:      'contacts',
								component: RouterView,
								props:     true,
								children:
								[
									{
										path:      '/',
										name:      'orders.contacts',
										component: Component('orders/Contacts'),
										props:     true,
									},

									{
										path:      'create',
										name:      'orders.contacts.create',
										component: Component('ccdb/ContactForm'),
										props:     true,
									},

									{
										path:      ':ccdbToOrderId',
										component: Component('ccdb/ContactLayout'),
										props:     true,
										children:
										[
											{
												path:      '/',
												name:      'orders.contacts.details',
												component: Component('ccdb/ContactDetails'),
												props:     true,
											},

											{
												path:      'update',
												name:      'orders.contacts.update',
												component: Component('ccdb/ContactForm'),
												props:     true,
											},

											{
												path:      'delete',
												name:      'orders.contacts.delete',
												component: Component('ccdb/ContactForm'),
												props:     true,
											},

											{
												path:      'contact-methods',
												component: RouterView,
												props:     true,
												children:
												[
													{
														path:      '/',
														name:      'orders.contacts.contact-methods.create',
														component: Component('ccdb/ContactMethodForm'),
														props:     true,
													},

													{
														path:      ':contactMethodId',
														name:      'orders.contacts.contact-methods.update',
														component: Component('ccdb/ContactMethodForm'),
														props:     true,
													},
												],
											},
										],
									},
								],
							},
						],
					},
				],
			},

			//------------------------------------------------------------
			// Accounts
			//------------------------------------------------------------

			{
				path:      'accounts',
				component: RouterView,
				meta:      { auth: true },
				children:
				[
					{
						path:      '/',
						name:      'accounts',
						component: Component('accounts/Listing'),
					},

					{
						path:      ':accountNumber',
						component: Component('accounts/AccountLayout'),
						props:     true,
						children:
						[
							{
								path:      '/',
								name:      'accounts.details',
								component: Component('accounts/Details'),
								props:     true,
							},
							
							
							{
								path:      'billing',
								name:      'accounts.billing',
								component: Component('accounts/Billing'),
								props:     true,
							},

							{
								path:      'invoices',
								name:      'accounts.invoices',
								component: Component('accounts/Invoices'),
								props:     true,
							},

							{
								path:      'payment',
								name:      'accounts.payment',
								component: Component('accounts/TruistPayment'),
								props:     true,
							},

							{
								path:      'transactions',
								name:      'accounts.transactions',
								component: Component('accounts/Transactions'),
								props:     true,
							},

							{
								path:      'contacts',
								name:      'accounts.contacts',
								component: Component('accounts/Contacts'),
								props:     true,
							},
						]
					},
				],
			},

			//------------------------------------------------------------
			// EDI
			//------------------------------------------------------------

			{
				path:     'edi',
				name:     'edi',
				component: Component('edi/Listing'),
				meta:     { auth: true },
			},

			//------------------------------------------------------------
			// Tickets
			//------------------------------------------------------------

			{
				path:      'tickets',
				component: RouterView,
				meta:      { auth: true },
				children:
				[
					{
						path:      '/',
						name:      'tickets',
						component: Component('tickets/Listing'),
					},

					{
						path:      'create/:orderId?',
						name:      'tickets.create',
						component: Component('tickets/Create'),
						props:     true,
					},

					{
						path:      ':ticketNumber?',
						name:      'tickets.detail',
						component: Component('tickets/Details'),
						props:     true,
					},

					{
						path:      'questions/admin',
						name:      'tickets.questions',
						component: Component('tickets/admin/Questions'),
						meta:      { auth: true, admin: true },
						props:     true,
					},

					{
						path:      'entrance/admin',
						name:      'tickets.entrance',
						component: Component('tickets/admin/Entrance'),
						meta:      { auth: true, admin: true },
						props:     true,
					},
				],
			},

			//------------------------------------------------------------
			// Scheduled Maintenance
			//------------------------------------------------------------

			{
				path:      'maintenance',
				component: RouterView,
				meta:      { auth: true },
				children:
				[
					{
						path:      '/',
						name:      'maintenance',
						component: Component('maintenance/Listing'),
					},

					{
						path:      ':workid',
						name:      'maintenance.customer',
						component: Component('maintenance/Create'),
					},

					{
						path:      'create/:orderId?',
						name:      'maintenance.create',
						component: Component('maintenance/Create'),
					},
				],
			},

			//------------------------------------------------------------
			// Network Diagnostics
			//------------------------------------------------------------

			{
				path:      'network',
				component: RouterView,
				meta:      { auth: true },
				children:
				[
					{
						path:      '/',
						name:      'network',
						component: Component('network/Diagnostics'),
					},
					{
						path:      ':orderId?',
						name:      'network.diagnostics',
						component: Component('network/Diagnostics'),
					},
				],
			},

			//------------------------------------------------------------
			// Statistics
			//------------------------------------------------------------

			{
				path:      'statistics',
				component: RouterView,
				meta:      { auth: true },
				children:
				[
					{
						path:      ':orderId',
						component: Component('statistics/OrderLayout'),
						props:     true,
						children:
						[
							{
								path:      ':statisticType(' + Constants.STATISTIC_TYPES.join('|') + ')?/:statisticPeriod(' + Constants.STATISTIC_PERIODS.join('|') + ')?',
								name:      'statistics.order',
								component: Component('statistics/OrderGraph'),
								props:     true,
							}
						],
					},
				],
			},

			//------------------------------------------------------------
			// Suggestions
			//------------------------------------------------------------

			{
				path:      'suggestions',
				name:      'suggestions',
				component: Component('suggestions/Submit'),
				meta:      { auth: true },
			},

			//------------------------------------------------------------
			// Contact Us
			//------------------------------------------------------------

			{
				path:      'contact/:contactId?',
				name:      'contact',
				component: Component('contact/Information'),
				props:     true,
				meta:      { auth: true },
			},

			{
				path:      'documentation/customersupport-na',
				name:      'customersupport-na',
				component: Component('documentation/CustomerSupportNA'),
				props:     true,
				meta:      { auth: true },
			},

			{
				path:      'documentation/customersupport-eu',
				name:      'customersupport-eu',
				component: Component('documentation/CustomerSupportEU'),
				props:     true,
				meta:      { auth: true },
			},

			//------------------------------------------------------------
			// IP Allocation Policy
			//------------------------------------------------------------

			{ 
				path:     'ipallocation',
				name:     'ipallocation',
				component: Component('documentation/IpAllocationPolicy'),
				meta:     { auth: true },
			},

			//------------------------------------------------------------
			// Acceptable Use Policy
			//------------------------------------------------------------

			{ 
				path:      'acceptable-use',
				name:      'acceptable-use',
				component: Component('documentation/AcceptableUsePolicy'),
			},

			{
				path:     'acceptableUse',
				redirect: 'acceptable-use',
			},

			//------------------------------------------------------------
			// Legal Matters
			//------------------------------------------------------------

			{
				path:     'legal',
				component: RouterView,
				children: 
				[
					{
						path: '/',
						name: 'legal',
						component: Component('documentation/LegalMatters'),
						props: route => route.query,
					},

					{
						path: 'unsubscribe',
						name: 'unsubscribe',
						component: Component('documentation/Unsubscribe'),
						props: true,
					},

					{
						path:     'ca-unsubscribe',
						redirect: 'unsubscribe',
					},
				],
			},

			//------------------------------------------------------------
			// Network Status
			//------------------------------------------------------------

			{
				path:      'network-status',
				name:      'network-status',
				component: Component('documentation/NetworkStatus'),
			},

			//------------------------------------------------------------
			// Global Users Guide
			//------------------------------------------------------------

			{
				path:      'guide',
				name:      'guide',
				component: Component('documentation/GlobalUsersGuide'),
			},

			{
				path:      'userguide',
				name:      'userguide',
				component: Component('documentation/UsersGuide'),
			},

			{
				path:      'userfaq',
				name:      'userfaq',
				component: Component('documentation/UsersFAQ'),
			},

			//------------------------------------------------------------
			// Payment Confirmation
			//------------------------------------------------------------

			{
				path:     'payment/confirmation',
				name:     'payment.confirmation',
				component: Component('payment/Confirmation'),
				meta:     { auth: true },
			},

			//------------------------------------------------------------
			// DNS Zones
			//------------------------------------------------------------

			{
				path:      'zones',
				component: RouterView,
				meta:      { auth: true },
				children:
				[
					{
						path:      '/',
						name:      'zones',
						component: Component('zones/Listing'),
					},

					{
						path:      ':zoneId',
						component: Component('zones/ZoneLayout'),
						props:     true,
						children:
						[
							{
								path:      '/',
								name:      'zones.details',
								component: Component('zones/ZoneDetails'),
								props:     true,
							},

							{
								path:      'file',
								name:      'zones.details.file',
								component: Component('zones/ZoneFile'),
								props:     true,
							},
							

							{
								path:      'update',
								name:      'zones.details.update',
								component: Component('zones/ZoneForm'),
								props:     true,
							},

							{
								path:      'records',
								component: RouterView,
								props:     true,
								children:
								[
									{
										path:      '/',
										name:      'zones.records',
										component: Component('zones/ZoneRecords'),
										props:     true,
									},

									{
										path:      'create',
										name:      'zones.records.create',
										component: Component('zones/ZoneRecordForm'),
										props:     true,
									},

									{
										path:      'edit',
										name:      'zones.records.edit',
										component: Component('zones/ZoneRecordAllForm'),
										props:     true,
									},

									{
										path:      ':zoneRecordId',
										name:      'zones.records.update',
										component: Component('zones/ZoneRecordForm'),
										props:     true,
									},
								],
							},

							{
								path:      'ip-addresses',
								component: RouterView,
								props:     true,
								children:
								[
									{
										path:      '/',
										name:      'zones.ip-addresses',
										component: Component('zones/ZoneIpAddresses'),
										props:     true,
									},

									{
										path:      'create',
										name:      'zones.ip-addresses.create',
										component: Component('zones/ZoneIpAddressForm'),
										props:     true,
									},

									{
										path:      ':zoneMasterId',
										name:      'zones.ip-addresses.update',
										component: Component('zones/ZoneIpAddressForm'),
										props:     true,
									},

									{
										path:      ':zoneMasterId/dig',
										name:      'zones.ip-addresses.dig',
										component: Component('zones/ZoneIpAddressDig'),
										props:     true,
									},

									{
										path:      ':zoneMasterId/whois',
										name:      'zones.ip-addresses.whois',
										component: Component('zones/ZoneIpAddressWhois'),
										props:     true,
									},
								],
							},
						],
					},
				],
			},

			//------------------------------------------------------------
			// IP Allocations
			//------------------------------------------------------------

			{
				path:      'allocations',
				component: RouterView,
				meta:      { auth: true },
				children:
				[
					{
						path:      '/',
						name:      'allocations',
						redirect: { name: 'allocations.ipv4' },
					},

					{
						path:      'ipv4',
						name:      'allocations.ipv4',
						component: Component('allocations/IPv4'),
					},
				],
			},

			//------------------------------------------------------------
			// Questionnaires
			//------------------------------------------------------------

			{
				path:      'questionnaires',
				component: RouterView,
				meta:      { auth: true },
				children:
				[
					{
						path:      '/',
						name:      'questionnaires',
						component: Component('questionnaires/Listing'),
					},

					{
						path:      'credit/:orderIds?',
						name:      'questionnaires.credit',
						component: Component('questionnaires/credit/Credit'),
						props:     true,
					},


					{
						path:      'credit',
						component: RouterView,
						meta:      { auth: true },
						children:
						[
							{
								path:      '/prepayInstructions',
								name:      'questionnaires.credit.prepay-instructions',
								component: Component('questionnaires/credit/PrepayInstructions'),
							},
						],
					},


					{
						path:      'ipv4/:orderId?',
						name:      'questionnaires.ipv4',
						component: Component('questionnaires/IPv4'),
						props:     true,
					},

					{
						path:      'ipv6/:orderId?',
						name:      'questionnaires.ipv6',
						component: Component('questionnaires/IPv6'),
						props:     true,
					},

					{
						path:      'bgp/:orderId?',
						name:      'questionnaires.bgp',
						component: Component('questionnaires/BGP'),
						props:     true,
					},

					{
						path:      'l2/:orderId?',
						name:      'questionnaires.l2',
						component: Component('questionnaires/L2'),
						props:     true,
					},

					{
						path:		'sdwan/:orderId?',
						name:		'questionnaires.sdwan',
						component:	Component('questionnaires/SDWAN'),
						props:		true,

					},
					{
						path:      'wave/:orderId?',
						name:      'questionnaires.wave',
						component: Component('questionnaires/Wave'),
						props:     true,
					},

				],
			},

			//------------------------------------------------------------
			// References
			//------------------------------------------------------------

			{
				path:      'references',
				component: RouterView,
				meta:      { auth: true, admin: true },
				children:
				[
					{
						path:      '/',
						name:      'references',
						component: Component('references/Listing'),
					},

					{
						path:      ':referenceNumber',
						component: Component('references/Layout'),
						props:     true,
						children:
						[
							{
								path:      '/',
								name:      'references.details',
								component: Component('references/Details'),
								props:     true,
							},
						]
					},
				],
			},

			//------------------------------------------------------------
			// Banner
			//------------------------------------------------------------

			{
				path:      'banners',
				component: RouterView,
				meta:      { auth: true, admin: true },
				children:
				[
					{
						path:      ':banner?',
						name:      'banners',
						component: Component('references/Banners'),
						props:     true,
					},
				],
			},

			//------------------------------------------------------------
			// Catch all to show 404 error page
			//------------------------------------------------------------

			{
				path:      '*',
				component: Component('404'),
			},
		]
	}
]

const router = new VueRouter
({
	routes,

	base:            '/',
	mode:            'history',
	linkActiveClass: 'active',

	scrollBehavior(to)
	{
		if ( to?.hash )
		{
			return {
				selector: to.hash,
			}
		}

		return { x: 0, y: 0 }
	},
})

const initialize = once
(
	() => Promise.all
	([
		new Promise
		(
			async resolve =>
			{
				if ( jwt.hasEmployeeToken() )
				{
					try
					{
						await store.dispatch(Types.SET_AUTH_EMPLOYEE)
					}
					catch(err)
					{}
				}

				resolve()
			}
		),

		new Promise
		(
			async resolve =>
			{
				if ( jwt.hasToken() )
				{
					try
					{
						await store.dispatch(Types.SET_AUTH_USER, { router })
					}
					catch(err)
					{}
				}

				resolve()
			}
		),
	])
)

router.beforeEach
(
	async (to, from, next) =>
	{
		store.dispatch(Types.IS_LOADING)
		store.dispatch(Types.UNSET_NO_PERMISSION)

		if ( from === VueRouter.START_LOCATION )
		{
			await initialize()
		} 

		const isAuthGuarded   = to.matched.some(route => route?.meta?.auth)
		const isGuestGuarded  = to.matched.some(route => route?.meta?.guest)
		const isAdminGuarded  = to.matched.some(route => route?.meta?.admin)
		const isAuthenticated = store.state.authUser.authenticated === true
		const isEDI           = store.state.authUser.is_edi === true
		const isAdminEmployee = isAuthenticated && store.state.authUser.employee_is_admin === true

		if ( isAuthGuarded && !isAuthenticated )
		{
			const path = ((value) => ( value?.includes('/auth') ) ? null : value )(to.fullPath)

			return next({ name: 'auth.login', query: { to: path } })
		}

		if ( isEDI && !to.fullPath.startsWith('/edi') && !to.fullPath.startsWith('/auth') && !to.fullPath.startsWith('/profile') )
		{
			return next({ name: 'edi' })
		}

		if ( isGuestGuarded && isAuthenticated )
		{
			return next({ name: 'orders' })
		}

		if ( isAdminGuarded && !isAdminEmployee )
		{
			store.dispatch(Types.SET_NO_PERMISSION)

			return next({ name: 'auth.login' })
		}

		return next()
	}
)

router.afterEach
(
	(to, from) =>
	{
		setTimeout(() => store.dispatch(Types.IS_NOT_LOADING), 200)

		/*
		const isAuthGuarded = to.matched.some(route => route?.meta?.auth)

		if ( isAuthGuarded && jwt.hasToken() )
		{
			store.dispatch(Types.REFRESH_TOKEN)
		}
		*/
	}
)

export default router